/**
 * Local mock API layer.
 *
 * Lets pages that normally need live CMS data (the quote page in particular)
 * run against fixtures stored in `src/mockApi/fixtures`, so you can work on
 * them without a real quote existing in the backend.
 *
 * Enabling it:
 *   - append `?mockApi=1` to any URL (persists for the browser tab), or
 *   - set `REACT_APP_MOCK_API=true` in `.env.local`.
 *
 * `?mockApi=0` turns it back off.
 *
 * Only GET requests made through `useGet` are intercepted. Anything without a
 * matching fixture still goes to the real API, so you can mock one endpoint at
 * a time.
 */
import searchResults from "./fixtures/searchResults";
import searchResultsHeader from "./fixtures/searchResultsHeader";
import domainsAndTenants from "./fixtures/domainsAndTenants";
import { getTenantById } from "./fixtures/tenants";
import { headerLinks, footerLinks } from "./fixtures/content";

// Stand-in leg id used when the quote page is opened without a real `lo`
// param. The fixtures ignore the id, so any value resolves to the same quote.
export const MOCK_LEG_ID = "mock-leg";

const STORAGE_KEY = "shyMockApi";
const QUERY_PARAM = "mockApi";

const isDevBuild = () => process.env.NODE_ENV !== "production";

const readToggle = () => {
    if (typeof window === "undefined") {
        return false;
    }

    // Build-time opt-in, so a deployed preview environment can use fixtures too.
    if (process.env.REACT_APP_MOCK_API === "true") {
        return true;
    }

    // The URL/session toggle is a local dev convenience only.
    if (!isDevBuild()) {
        return false;
    }

    let stored = null;

    try {
        stored = window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
        // Private browsing / storage disabled — fall back to the query param.
    }

    const param = new URLSearchParams(window.location.search).get(QUERY_PARAM);

    if (param !== null) {
        const enabled = param !== "0" && param.toLowerCase() !== "false";

        try {
            window.sessionStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
        } catch (e) {
            // Ignore — the param still applies for this page load.
        }

        return enabled;
    }

    return stored === "1";
};

export const isMockApiEnabled = () => readToggle();

/**
 * Fixture lookup by pathname. Each entry receives the parsed query string so
 * it can vary the response (e.g. tenant lookups). Query values that identify a
 * specific record — LegId, for instance — are deliberately ignored, so any
 * quote id returns the fixture.
 */
const routes = {
    "/api/frontend/v2/get-search-results": () => searchResults,
    "/api/frontend/get-search-results-header": () => searchResultsHeader,
    "/api/poweredbyshy/domains-and-tenants": () => domainsAndTenants,
    "/api/poweredbyshy/tenant-by-id": (params) => getTenantById(params.get("tenantId")),
    "/api/content/header": () => headerLinks,
    "/api/content/footer": () => footerLinks,
};

/**
 * @param {string} url - API path as passed to `useGet`, e.g. "/api/content/header?x=1"
 * @returns {{ data: * }|null} the mocked response, or null when the request
 *          should go to the real API.
 */
export const getMockResponse = (url) => {
    if (!url || typeof url !== "string" || !isMockApiEnabled()) {
        return null;
    }

    const [pathname, queryString = ""] = url.split("?");
    const route = routes[pathname.replace(/\/$/, "")];

    if (!route) {
        return null;
    }

    return { data: route(new URLSearchParams(queryString)) };
};

export default getMockResponse;
